import { type ShallowRef } from 'vue';

type TemplateRef = Readonly<ShallowRef<ComponentPublicInstance | null>>;

type ElementHeightOptions = {
  maxHeight?: number;
  transform?: () => number | undefined;
};

export const useElementHeight = (templateRef: TemplateRef, options: ElementHeightOptions) => {
  const height = ref(0);

  const calculateHeight = () => {
    const templateRefValue = unref(templateRef);

    if (!templateRefValue || templateRefValue.$el.style.display === 'none') {
      return 0;
    }

    // Allow passing in arbitrary logic
    if (options?.transform) {
      const transformResult = options.transform();

      if (typeof transformResult === 'number') {
        if (options.maxHeight) {
          return Math.min(transformResult, options.maxHeight);
        }

        return transformResult;
      }
    }

    const childNodes = templateRefValue.$el?.childNodes as NodeListOf<HTMLElement>;

    if (!childNodes?.length) {
      return 0;
    }

    // Accumulate the height of all child nodes
    const childHeight = [...childNodes].reduce<number>((acc, child) => acc + (child.offsetHeight || 0), 0);

    if (options.maxHeight) {
      return Math.min(childHeight, options.maxHeight);
    }

    return childHeight;
  };

  const updateHeight = () => (height.value = calculateHeight());

  // Run an initial update
  updateHeight();

  return {
    height,
    updateHeight,
  };
};
